// Chord voicings by search (F4.8 mandolin and bass, F4.9 voicing choice, F5.3 capo advice,
// F5.4 easy mode). For each chord, every fingering in a sliding fret window is checked against
// the instrument's rules; the playable ones are ranked by how easy they are.
import type { ChordQuality } from '../theory/chord';
import { QUALITIES } from '../theory/chord';
import { mod } from '../util/math';

export type FrettedInstrument = 'guitar' | 'ukulele' | 'mandolin' | 'bass';

/** Open-string MIDI notes, lowest string first (ukulele re-entrant: G4 C4 E4 A4). */
export const TUNINGS: Record<FrettedInstrument, number[]> = {
  guitar: [40, 45, 50, 55, 59, 64],
  ukulele: [67, 60, 64, 69],
  mandolin: [55, 62, 69, 76],
  bass: [28, 33, 38, 43],
};

interface Rules {
  /** Which strings may be left unplayed: none, only the lowest ones, or any. */
  mute: 'none' | 'low' | 'any';
  maxMuted: number;
  minStrings: number;
  maxStrings: number;
  /** The lowest sounding note must be the root. */
  rootInBass: boolean;
  /** Highest fret minus lowest fretted fret. */
  maxSpan: number;
  /** Bass plays root and fifth only. */
  rootAndFifthOnly: boolean;
}

const RULES: Record<FrettedInstrument, Rules> = {
  guitar: {
    mute: 'low',
    maxMuted: 2,
    minStrings: 4,
    maxStrings: 6,
    rootInBass: true,
    maxSpan: 3,
    rootAndFifthOnly: false,
  },
  ukulele: {
    mute: 'none',
    maxMuted: 0,
    minStrings: 4,
    maxStrings: 4,
    rootInBass: false,
    maxSpan: 3,
    rootAndFifthOnly: false,
  },
  mandolin: {
    mute: 'low',
    maxMuted: 1,
    minStrings: 3,
    maxStrings: 4,
    rootInBass: false,
    maxSpan: 4,
    rootAndFifthOnly: false,
  },
  bass: {
    mute: 'any',
    maxMuted: 3,
    minStrings: 1,
    maxStrings: 3,
    rootInBass: true,
    maxSpan: 3,
    rootAndFifthOnly: true,
  },
};

/** Highest fret a window may start at (12 frets is a full octave; enough for every chord). */
const MAX_BASE = 10;
/** Open strings are combined only with fretted notes at or below this fret. */
const OPEN_MAX_FRET = 5;
/** Voicings offered per chord. */
const MAX_VOICINGS = 6;

/** Fingers needed: one per fretted note, except that a barre on the lowest fret takes one. */
export function fingersFor(frets: number[]): number {
  const fretted = frets.filter((f) => f > 0);
  if (!fretted.length) return 0;
  const low = Math.min(...fretted);
  const first = frets.findIndex((f) => f === low);
  const atLow = frets.filter((f) => f === low).length;
  return isBarre(frets, low, first, atLow) ? fretted.length - atLow + 1 : fretted.length;
}

/** A barre covers every string from its first to the top, none of them open or muted. */
function isBarre(frets: number[], low: number, first: number, atLow: number): boolean {
  return atLow >= 2 && frets.slice(first).every((f) => f >= low);
}

/** Search ranking: low position, small span, few fingers, many strings. */
function rank(frets: number[]): number {
  const fretted = frets.filter((f) => f > 0);
  const low = fretted.length ? Math.min(...fretted) : 0;
  const high = fretted.length ? Math.max(...fretted) : 0;
  const sounding = frets.filter((f) => f >= 0).length;
  return 2 * low + (high - low) + 1.5 * fingersFor(frets) - 1.2 * sounding;
}

/**
 * How hard a fingering is for a beginner (easy mode, capo advice): fingers, barres, stretch
 * and position count; how many strings ring doesn't.
 */
export function difficulty(frets: number[]): number {
  const fretted = frets.filter((f) => f > 0);
  if (!fretted.length) return 0;
  const low = Math.min(...fretted);
  const high = Math.max(...fretted);
  const first = frets.findIndex((f) => f === low);
  const atLow = frets.filter((f) => f === low).length;
  // A barre is harder the more strings it holds down: two strings are easy, six are not.
  const covered = isBarre(frets, low, first, atLow) ? frets.length - first : 0;
  const barre = 0.75 * Math.max(0, covered - 2);
  return fingersFor(frets) + barre + 0.8 * (high - low) + 0.3 * (low - 1);
}

function search(instrument: FrettedInstrument, root: number, quality: ChordQuality): number[][] {
  const tuning = TUNINGS[instrument];
  const rules = RULES[instrument];
  const { intervals } = QUALITIES[quality];
  const r = mod(root, 12);
  const fifth = mod(r + 7, 12);
  const tones = new Set(
    rules.rootAndFifthOnly
      ? intervals.includes(7)
        ? [r, fifth]
        : [r]
      : intervals.map((iv) => mod(r + iv, 12)),
  );
  // Four-note chords may leave out the fifth; bass needs only the root.
  const required = rules.rootAndFifthOnly
    ? [r]
    : [...tones].filter((pc) => !(intervals.length === 4 && pc === fifth));
  const found = new Map<string, number[]>();

  for (let base = 1; base <= MAX_BASE; base++) {
    const options = tuning.map((open) => {
      const opts: number[] = rules.mute === 'none' ? [] : [-1];
      for (let f = base - 1; f <= base + rules.maxSpan; f++) {
        const fret = f === base - 1 ? 0 : f;
        if (tones.has(mod(open + fret, 12)) && !opts.includes(fret)) opts.push(fret);
      }
      return opts;
    });
    const frets = new Array<number>(tuning.length).fill(0);
    const visit = (s: number) => {
      if (s < tuning.length) {
        for (const f of options[s]!) {
          frets[s] = f;
          visit(s + 1);
        }
        return;
      }
      const sounding = frets.flatMap((f, i) => (f >= 0 ? [i] : []));
      if (sounding.length < rules.minStrings || sounding.length > rules.maxStrings) return;
      const lowest = sounding[0]!;
      if (rules.mute === 'low') {
        if (lowest > rules.maxMuted || sounding.length !== tuning.length - lowest) return;
      }
      if (rules.rootInBass && mod(tuning[lowest]! + frets[lowest]!, 12) !== r) return;
      const pcs = new Set(sounding.map((i) => mod(tuning[i]! + frets[i]!, 12)));
      if (!required.every((pc) => pcs.has(pc))) return;
      const fretted = frets.filter((f) => f > 0);
      if (fretted.length && Math.max(...fretted) - Math.min(...fretted) > rules.maxSpan) return;
      // Open strings ring only next to fretted notes low on the neck.
      if (fretted.length && frets.includes(0) && Math.min(...fretted) > OPEN_MAX_FRET) return;
      if (fingersFor(frets) > 4) return;
      found.set(frets.join(','), [...frets]);
    };
    visit(0);
  }

  const ranked = [...found.values()].sort((a, b) => rank(a) - rank(b));
  // Offer spread-out choices: the best voicing per lowest fret (open, low, middle, high).
  const byPosition = new Map<number, number[]>();
  for (const v of ranked) {
    const fretted = v.filter((f) => f > 0);
    const pos = fretted.length ? Math.min(...fretted) : 0;
    if (!byPosition.has(pos)) byPosition.set(pos, v);
  }
  const offered = [...byPosition.values()].sort((a, b) => rank(a) - rank(b)).slice(0, MAX_VOICINGS);
  // Always offer the easiest fingering too (a small F rather than only the full barre).
  const easiest = ranked.reduce<number[] | undefined>(
    (best, v) => (!best || difficulty(v) < difficulty(best) ? v : best),
    undefined,
  );
  if (easiest && !offered.includes(easiest))
    offered.splice(Math.min(1, offered.length), 0, easiest);
  return offered.slice(0, MAX_VOICINGS);
}

const cache = new Map<string, number[][]>();

/** Playable voicings, easiest-ranked first, at most one per neck position. */
export function searchVoicings(
  instrument: FrettedInstrument,
  root: number,
  quality: ChordQuality,
): number[][] {
  const key = `${instrument}:${mod(root, 12)}:${quality}`;
  let list = cache.get(key);
  if (!list) {
    list = search(instrument, root, quality);
    cache.set(key, list);
  }
  return list;
}

/** Pitch classes a fingering sounds (for tests and checks). */
export function soundingPitchClasses(
  instrument: FrettedInstrument,
  frets: number[],
  capo = 0,
): Set<number> {
  const out = new Set<number>();
  frets.forEach((f, s) => {
    if (f >= 0) out.add(mod(TUNINGS[instrument][s]! + capo + f, 12));
  });
  return out;
}
